import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * A link to a path the TanStack route tree does not know about yet.
 *
 * TanStack types `<Link to>` against the generated route tree, so linking to a
 * screen that still lives in the legacy react-router tree is a type error — which
 * is the typed routing working correctly, not a nuisance. Those paths are served
 * by the catch-all in routes/$.tsx, so they are addressed through its splat
 * parameter rather than as routes in their own right.
 *
 * Navigation still happens in-app: TanStack pushes the URL, the catch-all renders
 * the legacy tree, and react-router matches window.location from a fresh mount.
 *
 * This is migration scaffolding. Every time a screen becomes a real route, its
 * nav entry should move from LegacyLink to a plain typed <Link> — and when the
 * last one moves, this file is deleted. Treat a shrinking number of usages as the
 * progress metric.
 */
export const LegacyLink = ({
  to,
  className,
  title,
  children,
  ...rest
}: {
  /** An absolute app path, e.g. "/settings". */
  to: string;
  className?: string;
  title?: string;
  children: ReactNode;
  "aria-label"?: string;
}) => (
  <Link
    to="/$"
    params={{ _splat: to.replace(/^\//, "") }}
    className={className}
    title={title}
    {...rest}
  >
    {children}
  </Link>
);
